import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TextToRentCTA } from "@/components/TextToRentCTA";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Phone,
  Check,
  MapPin,
  Shield,
  Truck,
  Briefcase,
  DollarSign,
  CheckCircle,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VehicleDetailPage = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const response = await axios.get(`${API}/vehicles/${id}`);
        setVehicle(response.data);
      } catch (error) {
        console.error("Error fetching vehicle:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-custom section-padding">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-slate-200 mb-8"></div>
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="aspect-[4/3] bg-slate-200"></div>
              <div className="space-y-4">
                <div className="h-10 w-3/4 bg-slate-200"></div>
                <div className="h-6 w-1/2 bg-slate-200"></div>
                <div className="h-24 bg-slate-200"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-custom section-padding text-center">
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-4">
            Vehicle Not Found
          </h1>
          <Link to="/fleet">
            <Button>Back to Fleet</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vehicle-detail-page">
      <Header />

      <div className="container-custom section-padding">
        {/* Back Link */}
        <Link
          to="/fleet"
          className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8 transition-colors duration-200"
          data-testid="back-to-fleet"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Fleet
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="relative">
            <img
              src={vehicle.image_url}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              className="w-full aspect-[4/3] object-cover shadow-xl"
              data-testid="vehicle-image"
            />
            {/* Availability Badge */}
            <div className="absolute top-4 left-4">
              {vehicle.available ? (
                <span className="badge-available">
                  <Check className="w-3 h-3 mr-1" />
                  Available
                </span>
              ) : (
                <span className="badge-unavailable">Not Available</span>
              )}
            </div>
          </div>

          {/* Details */}
          <div>
            {/* Year & Color */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                {vehicle.year}
              </span>
              <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
              <span className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                {vehicle.color}
              </span>
            </div>

            {/* Make & Model */}
            <h1
              className="font-heading font-black text-3xl md:text-4xl text-slate-900 mb-4"
              data-testid="vehicle-title"
            >
              {vehicle.make} {vehicle.model}
            </h1>

            {/* Price */}
            <div className="bg-slate-900 text-white p-6 mb-6 inline-block">
              <div className="flex items-baseline gap-2">
                <span className="font-mono font-bold text-4xl text-amber-400">$200</span>
                <span className="text-slate-400 text-lg">/week</span>
              </div>
              <p className="text-slate-400 text-sm mt-1">No deposit required · No credit checks</p>
            </div>

            {/* Description */}
            <p className="text-slate-600 text-lg mb-6" data-testid="vehicle-description">
              {vehicle.description}
            </p>

            {/* Features */}
            {vehicle.features && vehicle.features.length > 0 && (
              <div className="mb-8">
                <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-slate-900 mb-3">
                  Features
                </h3>
                <div className="flex flex-wrap gap-2">
                  {vehicle.features.map((feature, i) => (
                    <span
                      key={i}
                      className="text-sm px-3 py-2 bg-slate-100 text-slate-700 font-medium"
                      data-testid={`feature-${i}`}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="sms:8592703755?body=car%20rental"
                data-testid="vehicle-cta"
              >
                <Button
                  size="lg"
                  className="bg-primary text-white uppercase tracking-wide font-bold text-base px-8 py-6 btn-brutalist w-full sm:w-auto"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Text to Book This Car
                </Button>
              </a>
            </div>

            <p className="text-slate-500 text-sm mt-4">
              Text "car rental" to{" "}
              <a href="sms:8592703755?body=car%20rental" className="font-mono font-bold text-primary">
                (859) 270-3755
              </a>{" "}
              to book
            </p>
          </div>
        </div>

        {/* Requirements Section */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <div className="bg-white p-6 border border-slate-200">
            <MapPin className="w-6 h-6 text-primary mb-3" />
            <h3 className="font-heading font-bold text-slate-900 mb-2">Service Area</h3>
            <p className="text-slate-600 text-sm">
              Lexington Metropolitan Area only.
            </p>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <Shield className="w-6 h-6 text-primary mb-3" />
            <h3 className="font-heading font-bold text-slate-900 mb-2">Requirements</h3>
            <p className="text-slate-600 text-sm">
              Valid driver's license. Must be 25 years of age or older.
            </p>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <Truck className="w-6 h-6 text-primary mb-3" />
            <h3 className="font-heading font-bold text-slate-900 mb-2">Approved Uses</h3>
            <p className="text-slate-600 text-sm">
              Grub Hub, Uber Eats, Instacart, and daily work commutes.
            </p>
          </div>
        </div>
      </div>

      <Footer />
      <TextToRentCTA />
    </div>
  );
};

export default VehicleDetailPage;
